import DetectRTC from "detectrtc";
import reduxStore from "../../configureEnvStore";
import { PublicVariables } from "../../constants/PublicVariables";
import {
    setIsAndroidBrowserCheckModalOpen,
    setIsAndroidWebViewModalOpen,
    setIsIOSBrowserCheckModalOpen,
    setIsLowerVersionModalOpen,
    setIsUnSupportedBrowserModalOpen,
    setIsWebViewFoundModalOpen,
} from "../../reducers/common-reducer";
import { getExactBrowserName, getMicrosoftEdgeBrowserVersion, isMicrosoftEdgeBrowser } from "../../shared/utility";
import { BROWSER, SUPPORTED_BROWSER_VERSION, SUPPORTED_IOS_DEVICE } from "../../constants/BottomMenuConstants";
import { HELP_ARTICLE } from "../../constants/LDConstants";
import { isBrowserSupportScreenShare } from "../Twilio/ScreenShare/ScreenShareUtils";
import navigatorService from "../../services/navigatorService";
import Localize from "../../locale/Localize";
import { handleEndCallFunc } from "../Twilio/Room/roomFunction";
import Reducers from "../../reducers/Reducers";

import { configureStore } from "@reduxjs/toolkit";

// Desktop supported
export const desktopSupportedBrowser = () => {
    const OSName = DetectRTC.osName.toLowerCase();
    const browserVersion = DetectRTC.browser.version;
    const browserName = DetectRTC.browser.name.toLowerCase();
    let supported = false;

    if (browserName == BROWSER.CHROME && browserVersion >= SUPPORTED_BROWSER_VERSION.CHROME) {
        if (isMicrosoftEdgeBrowser()) {
            const edgeVersion = getMicrosoftEdgeBrowserVersion();
            const edgeVersionNum = parseFloat(edgeVersion);
            if (edgeVersionNum >= SUPPORTED_BROWSER_VERSION.EDGE) {
                HELP_ARTICLE.BROWSER_SPECIFIC_SOLUTION_LINK = HELP_ARTICLE.DESKTOP_CHROME_ARTICLES_NUMBER;
                supported = true;
            }
        } else {
            HELP_ARTICLE.BROWSER_SPECIFIC_SOLUTION_LINK = HELP_ARTICLE.DESKTOP_CHROME_ARTICLES_NUMBER;
            supported = true;
        }
    } else if (browserName == BROWSER.FIREFOX && browserVersion >= SUPPORTED_BROWSER_VERSION.FIREFOX) {
        HELP_ARTICLE.BROWSER_SPECIFIC_SOLUTION_LINK = HELP_ARTICLE.FIREFOX_ARTICLES_NUMBER;
        supported = true;
    } else if (OSName.indexOf("mac") > -1 && browserName == BROWSER.SAFARI && browserVersion > SUPPORTED_BROWSER_VERSION.SAFARI) {
        HELP_ARTICLE.BROWSER_SPECIFIC_SOLUTION_LINK = HELP_ARTICLE.SAFARI_MAC_ARTICLES_NUMBER;
        supported = true;
    }
    return supported;
};

/** checks if browser is supported or not */
export const checkSupportedBrowsers = () => {
    let isSupported = true;
    if (navigatorService.isMobileDeviceOrIpadOs()) {
        //mobile devices
        if (!isValidDevice()) {
            reduxStore.dispatch(setIsLowerVersionModalOpen(true));
            isSupported = false;
        } else if (PublicVariables.isIOSDevice) {
            //iOS
            if (navigatorService.isUIWebView()) {
                return true; // UIWebView is supported
            }
            if (!navigatorService.isSafari()) {
                reduxStore.dispatch(setIsIOSBrowserCheckModalOpen(true));
                isSupported = false;
            } else if (!navigator.mediaDevices) {
                reduxStore.dispatch(setIsWebViewFoundModalOpen(true));
                isSupported = false;
            }
        } else if (DetectRTC.osName == "Android") {
            //android
            const hasUserAcceptedWebView = reduxStore.getState().commonReducer.hasUserAcceptedWebView;
            if (navigatorService.isAndroidWebView() && !hasUserAcceptedWebView) {
                // WebView detected - show recommendation modal and block until user decides
                reduxStore.dispatch(setIsAndroidWebViewModalOpen(true));
                isSupported = false;
            } else if (!navigatorService.isChrome() && !navigatorService.isAndroidWebView()) {
                // Not a supported browser (and not a WebView)
                reduxStore.dispatch(setIsAndroidBrowserCheckModalOpen(true));
                isSupported = false;
            }
            if (isSupported && !isValidAndroidBrowser()) {
                reduxStore.dispatch(setIsLowerVersionModalOpen(true));
                isSupported = false;
            }
        }
    } else if (!desktopSupportedBrowser()) {
        //desktop = chrome,safari,FF
        reduxStore.dispatch(setIsUnSupportedBrowserModalOpen(true));
        isSupported = false;
    } else {
        isBrowserSupportScreenShare();
    }

    return isSupported;
};

//check browser is lower version
export const isValidDevice = () => {
    const OSName = DetectRTC.osName.toLowerCase();
    return !(OSName == "ios" && parseFloat(DetectRTC.osVersion) < SUPPORTED_IOS_DEVICE);
};

///supported android browser with version
export const isValidAndroidBrowser = () => {
    const OSName = DetectRTC.osName.toLowerCase();
    let { browserName, browserVersion } = getExactBrowserName();
    browserName = browserName.toLowerCase();
    browserVersion = parseFloat(browserVersion);

    if (OSName === "android") {
        if (browserName === "samsung internet") {
            const samsungBrowserVersion = DetectRTC.browser.version;
            return samsungBrowserVersion > SUPPORTED_BROWSER_VERSION.ANDROID_SAMSUNG_CHROMIUM;
        }
        if (browserName === BROWSER.CHROME) {
            return browserVersion > SUPPORTED_BROWSER_VERSION.ANDROID_CHROME;
        }
    }

    return true;
};

function handleReloadClickFn() {
    handleEndCallFunc();
    setTimeout(() => {
        window.location.reload();
    }, 500);
}

if (typeof window !== "undefined") {
    window.handleReloadClickFn = handleReloadClickFn;
}

export const setPermissionPopupText = () => {
    let locationPermissionText = "";
    const fullText = Localize[PublicVariables.currentLanguageObj.languageCode]["go_to_browser_settings"];
    const click_here_label = Localize[PublicVariables.currentLanguageObj.languageCode]["click_here_label"];
    let regEx = new RegExp(click_here_label, "ig");
    const anchorTag = `<button class='btn default-btn-link' id="clickhere" onclick="javascript: handleReloadClickFn();" >${click_here_label}</button>`;
    locationPermissionText = fullText.replace(regEx, anchorTag);
    return { __html: locationPermissionText };
};

//make common mockStore function for unit test files
export const mockStore = (initialState) => {
    return configureStore({
        reducer: Reducers,
        middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(),
        preloadedState: initialState,
    });
};

export const stopPropagationAndPreventEvent = (e) => {
    e.stopPropagation();
    e.preventDefault();
};

export const removeDragHoverClass = (domRef) => {
    domRef?.current?.classList?.remove("drag-hover");
};

export const addDragHoverClass = (domRef) => {
    domRef?.current?.classList?.add("drag-hover");
};
